import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import type { AfterViewInit } from '@angular/core';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  forwardRef,
  inject,
  InjectionToken,
  Injector,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { has, isArray, isEqual, orderBy, sumBy, uniqBy } from 'lodash';
import {
  distinctUntilChanged,
  fromEventPattern,
  map,
  skip,
  startWith,
} from 'rxjs';
import { OnyxAvatarComponent } from '../../../components/avatar/onyx-avatar/onyx-avatar.component';
import { OnyxIconButtonComponent } from '../../../components/buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxShortcutsComponent } from '../../../components/buttons/onyx-shortcuts/onyx-shortcuts.component';
import { OnyxEmojiComponent } from '../../../components/emoji/onyx-emoji/onyx-emoji.component';
import { OnyxFlagComponent } from '../../../components/flags/onyx-flag/onyx-flag.component';
import { OnyxIconComponent } from '../../../components/icons/onyx-icon/onyx-icon.component';
import { OnyxSearchComponent } from '../../../components/search/onyx-search/onyx-search.component';
import { OnyxSpinnerComponent } from '../../../components/spinner/onyx-spinner/onyx-spinner.component';
import type {
  OnyxDropdownConfig,
  OnyxOption,
  OnyxOptionsGroup,
} from '../../../directives/interfaces';
import { OnyxTooltipDirective } from '../../../directives/onyx-tooltip.directive';
import { OnyxOverlayPosition } from '../../../enums';
import { DropdownHelper } from '../../../helpers/dropdown.helper';
import { UtilityHelper } from '../../../helpers/utility.helper';
import { OnyxHighlightPipe } from '../../../pipes/onyx-highlight.pipe';
import { I18N_NAMESPACE } from '../../constants';

export const ONYX_DROPDOWN_CONFIG = new InjectionToken<OnyxDropdownConfig<any>>(
  'Dropdown configuration',
);

@Component({
  selector: 'onyx-dropdown-overlay',
  imports: [
    NgTemplateOutlet,
    OnyxIconComponent,
    OnyxSpinnerComponent,
    OnyxHighlightPipe,
    ReactiveFormsModule,
    TranslatePipe,
    forwardRef(() => OnyxSearchComponent),
    OnyxShortcutsComponent,
    OnyxTooltipDirective,
    OnyxIconButtonComponent,
    DecimalPipe,
    OnyxFlagComponent,
    OnyxAvatarComponent,
    NgTemplateOutlet,
    OnyxEmojiComponent,
  ],
  templateUrl: './onyx-dropdown-overlay.component.html',
  styleUrl: './onyx-dropdown-overlay.component.scss',
  host: {
    '[style.max-height]': `config.maxHeight`,
    '[style.min-width.px]': `minWidth()`,
    '(input)': `lockWidth()`,
    '(keydown.escape)': `close()`,
    '(keydown.shift.tab)': `close()`,
    '(keydown.tab)': `close()`,
  },
})
export class OnyxDropdownOverlayComponent<T> implements AfterViewInit {
  protected readonly config =
    inject<OnyxDropdownConfig<T>>(ONYX_DROPDOWN_CONFIG);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  protected readonly I18N = `${I18N_NAMESPACE}.dropdown`;

  protected readonly OnyxOverlayPosition = OnyxOverlayPosition;

  protected groups = signal<OnyxOptionsGroup<T>[] | null | undefined>(null);
  protected loading = signal(false);
  protected toggleAllLoading = signal(false);
  protected minWidth = signal<number | null>(null);

  protected isEmpty = computed(
    () =>
      !this.groups() || this.groups()!.every((group) => !group.options.length),
  );
  protected value = computed<T[]>(() =>
    this.selectedOptions().map((option) => option.value),
  );
  protected selectedAny = computed(
    () =>
      this.groups()
        ?.flatMap((group) => group.options)
        .some((option) => !!option._selected) ?? false,
  );
  protected selectedAll = computed(
    () =>
      this.groups()
        ?.flatMap((group) => group.options)
        .every((option) => !!option._selected || !!option.disabled) ?? false,
  );
  protected currentItems = computed(() =>
    sumBy(this.groups() ?? [], (group) => group.options.length),
  );

  private searchRef = viewChild(OnyxSearchComponent);
  private groupRefs = viewChildren<ElementRef<HTMLElement>>('groupRef');

  private selectedOptions = signal<OnyxOption<T>[]>([]);

  constructor() {
    const config = this.config;

    const element = config.elementRef;
    if (element && config.width === 'inherit') {
      fromEventPattern<void>(
        (handler) => {
          const resizeObserver = new ResizeObserver(() => handler());
          resizeObserver.observe(element);

          return resizeObserver;
        },
        (_, resizeObserver?: ResizeObserver) => resizeObserver?.disconnect(),
      )
        .pipe(startWith(undefined), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.minWidth.set(Math.max(240, element.clientWidth)));
    }

    effect(() => {
      const page = this.config.page();
      const totalItems = this.config.totalItems?.();
      if (totalItems == null) return;

      const reachedLimit = this.currentItems() >= page * this.config.limit;
      const allLoaded = this.currentItems() === totalItems;
      if (allLoaded) this.toggleAllLoading.set(false);
      if (reachedLimit || allLoaded) this.loading.set(false);
    });

    this.setOptions(config.values);

    toObservable(this.value)
      .pipe(
        distinctUntilChanged(isEqual),
        skip(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.config.selectOptions(this.selectedOptions());
        if (!this.config.multiple) this.config.close();
      });
  }

  public ngAfterViewInit(): void {
    effect(
      () => {
        if (this.isEmpty()) return;

        setTimeout(() =>
          this.checkAndLoadMore(this.elementRef.nativeElement, true),
        );
      },
      { injector: this.injector },
    );
  }

  public onScroll(event: Event): void {
    if (this.loading()) return;

    this.checkAndLoadMore(event.target as HTMLElement, false);
  }

  public focus(): void {
    this.focusSearch();
  }

  protected lockWidth(): void {
    this.minWidth.set(this.elementRef.nativeElement.offsetWidth);
  }

  protected close(): void {
    this.config.close();
  }

  protected focusElement(): void {
    this.config.focusElement();
  }

  protected focusSearch(): void {
    if (this.config.search) setTimeout(() => this.searchRef()?.focus());
  }

  protected toggleAll(selectAll?: boolean): void {
    selectAll ??= !this.selectedAll();

    const updateGroups = (selectAll: boolean) => {
      this.groups.update((groups) =>
        groups?.map((group) => ({
          ...group,
          options: group.options.map((option) => ({
            ...option,
            _selected: option.disabled ? option._selected : selectAll,
            _hidden: !selectAll,
          })),
        })),
      );
    };

    if (!selectAll) {
      updateGroups(false);
      this.selectedOptions.set([]);
      this.toggleAllLoading.set(false);
      return;
    }

    if (this.currentItems() < (this.config.totalItems?.() ?? 0)) {
      this.loadAndSelectOptions();
      return;
    }

    updateGroups(true);
    this.selectedOptions.set(
      this.groups()
        ?.flatMap((group) => group.options)
        .filter((option) => option._selected && !option.implied) ?? [],
    );
    this.toggleAllLoading.set(false);
  }

  protected toggleOption(value: T, force?: boolean): void {
    if (!this.config.optional && !this.config.multiple) force = true;

    this.groups.update((groups) =>
      groups?.map((group) => ({
        ...group,
        options: group.options.map((option) => {
          const toggle = isEqual(option.value, value)
            ? (force ?? !option._selected)
            : this.config.multiple
              ? null
              : false;
          if (toggle === null) return option;

          if (toggle) {
            this.selectedOptions.update((selectedOptions) => {
              if (this.config.multiple) {
                return uniqBy(
                  [...selectedOptions, option],
                  (option) => option.value,
                );
              }
              return [option];
            });
          } else {
            this.selectedOptions.update((selectedOptions) =>
              selectedOptions.filter(
                (selectedOption) =>
                  !isEqual(
                    this.getCompareValue(selectedOption.value),
                    this.getCompareValue(option.value),
                  ),
              ),
            );
          }

          return {
            ...option,
            _selected: toggle,
          };
        }),
      })),
    );

    if (!this.config.multiple) {
      afterNextRender(() => this.config.close(), { injector: this.injector });
    }
  }

  protected clear(): void {
    this.toggleAll(false);
    setTimeout(() => this.config.close());
  }

  private checkAndLoadMore(element: HTMLElement, allGroups: boolean): void {
    const totalItems = this.config.totalItems?.();
    if (totalItems == null || this.isEmpty()) return;
    if (this.currentItems() >= totalItems) return;

    const containerBottom = element.getBoundingClientRect().bottom;
    const groupRefs = allGroups ? this.groupRefs() : [this.groupRefs()[0]];

    for (const groupRef of groupRefs) {
      const group = groupRef.nativeElement as HTMLElement;
      const lastItem = group.lastElementChild as HTMLElement | null;
      if (!lastItem) continue;

      const { top, height } = lastItem.getBoundingClientRect();
      if (top - height <= containerBottom) {
        this.config.page.update((page) => page + 1);
        this.loading.set(true);
        break;
      }
    }
  }

  private getCompareValue(value: T): unknown {
    return DropdownHelper.getCompareValue(value, this.config.compareKey);
  }

  private setOptions(initialValues: T[]): void {
    const source$ = UtilityHelper.maybeAsyncToObservable(this.config.options);

    let first = true;
    source$
      .pipe(
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        map((data): OnyxOptionsGroup<T>[] | null => {
          if (data == null || !isArray(data)) {
            return null;
          } else if (has(data[0], 'options')) {
            return data as OnyxOptionsGroup<T>[];
          }
          return [
            {
              id: '0.default',
              subheading: this.config.subheading,
              subheadingTemplateRef: this.config.subheadingTemplateRef,
              options: data as OnyxOption<T>[],
            },
          ];
        }),
        map((groups) => {
          const values = first ? initialValues : this.value();

          return groups
            ?.map((group) => ({
              ...group,
              options: group.options
                .filter((option) => !option._hidden)
                .map((option): OnyxOption<T> => ({
                  ...option,
                  disabled: option.disabled || option.implied,
                  _selected:
                    option.implied ||
                    values.some((value) =>
                      isEqual(
                        this.getCompareValue(value),
                        this.getCompareValue(option.value),
                      ),
                    ),
                })),
            }))
            ?.filter((group) => group.options.length);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((groups) => {
        this.groups.set(
          first && this.config.multiple
            ? groups?.map((group) => ({
                ...group,
                options: orderBy(
                  group.options,
                  [
                    (option) => option.disabled && option._selected,
                    (option) => option._selected,
                    (option) => !option._selected,
                  ],
                  ['desc', 'desc', 'desc'],
                ),
              }))
            : groups,
        );

        if (!first || this.isEmpty()) return;

        first = false;

        this.selectedOptions.set(
          DropdownHelper.mapToOptions(groups)?.filter(
            (option) => option._selected && !option.implied,
          ) ?? [],
        );
        this.focusSearch();
      });
  }

  private loadAndSelectOptions(): void {
    const totalItems = this.config.totalItems?.();
    if (!totalItems) return;

    this.toggleAllLoading.set(true);

    const loadPage = () => {
      if (this.currentItems() >= totalItems) {
        this.toggleAll(true);
        return;
      }

      this.config.page.set(
        Math.floor(this.currentItems() / this.config.limit) + 1,
      );
      setTimeout(loadPage);
    };

    loadPage();
  }
}
